import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const blank = { code: "", description: "", discount_type: "percent", amount: 10, min_amount: 0, max_uses: 0, active: true, is_public: true, expires_at: "" };

export default function AdminPromos() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = async () => { const { data } = await api.get("/admin/promos"); setRows(data); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!edit.code) return toast.error("Code required");
    const payload = { ...edit, amount: Number(edit.amount), min_amount: Number(edit.min_amount), max_uses: Number(edit.max_uses) };
    try { if (edit.id) await api.patch(`/admin/promos/${edit.id}`, payload);
          else await api.post("/admin/promos", payload);
          toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const rm = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/promos/${id}`); load(); };
  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Promo codes</h1><p className="text-slate-500 mt-1">Create and manage discount codes used at checkout.</p></div>
        <Button data-testid="promo-new" onClick={() => setEdit({ ...blank })} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full"><Plus className="w-4 h-4 mr-1" /> New promo</Button>
      </div>
      <Card className="mt-5 overflow-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Discount</TableHead><TableHead>Min amount</TableHead><TableHead>Uses</TableHead><TableHead>Expires</TableHead><TableHead>Active</TableHead><TableHead>Public</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} data-testid={`promo-row-${r.id}`}>
                <TableCell className="font-mono font-bold">{r.code}</TableCell>
                <TableCell>{r.discount_type === "percent" ? `${r.amount}%` : `$${r.amount}`}</TableCell>
                <TableCell>${r.min_amount}</TableCell>
                <TableCell>{r.uses}{r.max_uses ? `/${r.max_uses}` : ""}</TableCell>
                <TableCell className="text-xs text-slate-500">{r.expires_at || "—"}</TableCell>
                <TableCell><Switch checked={r.active} onCheckedChange={v => api.patch(`/admin/promos/${r.id}`,{active:v}).then(load)} /></TableCell>
                <TableCell><Switch data-testid={`promo-public-${r.id}`} checked={r.is_public !== false} onCheckedChange={v => api.patch(`/admin/promos/${r.id}`,{is_public:v}).then(load)} /></TableCell>
                <TableCell className="flex gap-2"><Button size="sm" variant="outline" onClick={() => setEdit({...r})}><Pencil className="w-3.5 h-3.5 mr-1"/> Edit</Button><Button size="sm" variant="ghost" onClick={() => rm(r.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button></TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-10">No promo codes</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        {edit && (
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading text-xl text-[var(--brand-accent)]">{edit.id ? "Edit promo" : "New promo"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Code</Label><Input value={edit.code} onChange={e=>setEdit({...edit,code:e.target.value.toUpperCase()})}/></div>
              <div>
                <Label>Type</Label>
                <Select value={edit.discount_type} onValueChange={v=>setEdit({...edit,discount_type:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">Percent (%)</SelectItem><SelectItem value="fixed">Flat ($)</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Amount</Label><Input type="number" step="0.01" value={edit.amount} onChange={e=>setEdit({...edit,amount:e.target.value})}/></div>
              <div><Label>Min order amount ($)</Label><Input type="number" step="0.01" value={edit.min_amount} onChange={e=>setEdit({...edit,min_amount:e.target.value})}/></div>
              <div><Label>Max uses (0 = unlimited)</Label><Input type="number" value={edit.max_uses} onChange={e=>setEdit({...edit,max_uses:e.target.value})}/></div>
              <div><Label>Expires (ISO date)</Label><Input type="date" value={(edit.expires_at || "").slice(0,10)} onChange={e=>setEdit({...edit,expires_at:e.target.value})}/></div>
              <div className="col-span-2"><Label>Description</Label><Input value={edit.description||""} onChange={e=>setEdit({...edit,description:e.target.value})}/></div>
              <div className="col-span-2 flex items-center justify-between"><Label>Active</Label><Switch checked={!!edit.active} onCheckedChange={v=>setEdit({...edit,active:v})}/></div>
              <div className="col-span-2 flex items-center justify-between">
                <div>
                  <Label>Public (show to customers)</Label>
                  <div className="text-[11px] text-slate-500 mt-0.5">Surfaces this code as a chip on the Review page under "Available offers".</div>
                </div>
                <Switch data-testid="promo-edit-public" checked={edit.is_public !== false} onCheckedChange={v=>setEdit({...edit,is_public:v})}/>
              </div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={()=>setEdit(null)}>Cancel</Button><Button data-testid="promo-save" onClick={save} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">{edit.id ? "Save" : "Create"}</Button></DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
